// MultiLevelMenubar.js
import React, { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

const MultiLevelMenubar = ({ data, menuWidth = "20rem" }) => {
  const [path, setPath] = useState([]); // Stack of selected category objects
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsMenuOpen(false);
        setPath([]);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Panels array: first panel is top-level (items), then every path item is a panel.
  const panels = [{ items: data, title: "Categories" }, ...path];
  const panelCount = panels.length; // Used in sizing & translation
  const currentIndex = path.length; // Which panel is visible (0-based)

  // Translate percentage of the TRACK element
  const translatePercent = panelCount > 0 ? (currentIndex * 100) / panelCount : 0;

  const handleItemClick = (item) => {
    // If item has subcategories, drill down
    if (Array.isArray(item.subcategories) && item.subcategories.length > 0) {
      setPath((prevPath) => [...prevPath, { items: item.subcategories, title: item.name || item.category }]);
    } else {
      // Leaf selected — do something (e.g., navigate or close menu)
      console.log("Selected item:", item.name || item.category);
      setIsMenuOpen(false);
      setPath([]);
    }
  };

  const goBackOne = () => {
    setPath((prevPath) => prevPath.slice(0, -1));
  };

  return (
    <div className="position-relative" style={{ width: menuWidth }} ref={wrapperRef}>
      <button
        className="btn btn-primary rounded-pill px-4"
        onClick={() => {
          setIsMenuOpen((prevState) => !prevState);
          if (!isMenuOpen) setPath([]); // Reset when opening
        }}
      >
        Shop All
      </button>

      {isMenuOpen && (
        <div
          className="dropdown-menu show p-0 shadow"
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            width: menuWidth,
            borderRadius: "0.5rem",
            overflow: "hidden",
          }}
        >
          {/* TRACK */}
          <div
            style={{
              display: "flex",
              width: `${panelCount * 100}%`, // Track width = panels * 100%
              transform: `translateX(-${translatePercent}%)`, // Move by computed percent of track width
              transition: "transform 300ms ease",
            }}
          >
            {panels.map((level, idx) => {
              const items = level.items || [];
              // Each panel's width is 100 / panelCount percent of TRACK width
              const panelFlexBasis = `${100 / panelCount}%`;

              // Show back button only on the CURRENT visible panel (idx === currentIndex) and not the first
              const showBack = idx > 0 && idx === currentIndex;

              return (
                <div
                  key={`${level.title || "panel"}-${idx}`}
                  style={{
                    flex: `0 0 ${panelFlexBasis}`,
                    maxWidth: `${panelFlexBasis}`,
                    boxSizing: "border-box",
                    background: "#fff",
                    minHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  <div className="p-3 border-bottom d-flex align-items-center">
                    {showBack && (
                      <button
                        className="btn btn-link text-decoration-none p-0 me-2"
                        onClick={goBackOne}
                      >
                        ← Back
                      </button>
                    )}
                    <h6 className="m-0">
                      {idx === 0 ? "Categories" : level.title}
                    </h6>
                  </div>

                  <ul className="list-group list-group-flush">
                    {items.map((item, i) => {
                      const label = item.name || item.category || String(item);
                      const hasChildren =
                        Array.isArray(item.subcategories) && item.subcategories.length > 0;
                      return (
                        <li key={`${label}-${i}`} className="list-group-item p-0">
                          <button
                            className="d-flex justify-content-between align-items-center w-100 text-start px-3 py-2 btn btn-light border-0"
                            onClick={() => handleItemClick(item)}
                          >
                            <span>{label}</span>
                            {hasChildren && <span className="fw-bold text-secondary">&gt;</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiLevelMenubar;